import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Outlet, useNavigate, useParams } from "react-router-dom"
import { AnalyticsService } from "services/AnalyticsService"
import { ProfileOfferService } from "services/api-service"
import { ApplicationType } from "services/api-service/types"
import { SentryService } from "services/SentryService"
import styled from "styled-components"
import { Box, Row } from "ui/components/atoms/_atoms"
import Switch from "ui/components/atoms/switch"
import { Label, P } from "ui/components/atoms/typography"
import OfferApplicationList from "views/Profile/Offers/OfferApplications/components/OfferApplicationList"

const OfferApplicationModal = () => {
  const { t } = useTranslation(["panel"])
  const navigate = useNavigate()
  const { offerId = "" } = useParams()

  const [isNotRead, setIsNotRead] = useState<boolean>(false)

  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [count, setCount] = useState<number | null>(null)
  const [data, setData] = useState<ApplicationType[]>([])

  const fetchData = useCallback(
    async (newPage: number, isNotRead: boolean) => {
      try {
        setLoading(true)
        const { content, totalPages, totalElements } =
          await ProfileOfferService.getOfferApplications(`${offerId}`, isNotRead, newPage)
        setData(content)
        setTotalPages(totalPages)
        setCount(totalElements)
      } catch (e) {
        SentryService.error("[ERROR ProfileOfferService.getOfferApplications]:", e)
      } finally {
        setLoading(false)
      }
    },
    [offerId],
  )

  useEffect(() => {
    fetchData(page, isNotRead)
  }, [fetchData, page, isNotRead])

  useEffect(() => {
    AnalyticsService.track("panel_offer_applications_open")
  }, [])

  return (
    <>
      <Wrapper>
        <Row $marginBottom="S" $justifyContent="space-between" $alignItems="flex-end">
          <Box>
            {!loading && (
              <P $marginBottom="0">
                {t("offers.offer.candidates.count")} {count ?? "0"}
              </P>
            )}
          </Box>
          <SwitchWrapper>
            <Label $marginBottom="0" $marginRight="XS">
              {t("offers.offer.candidates.only-new-cadidates")}
            </Label>
            <Switch
              name="paymentContract"
              value={isNotRead}
              onChange={({ name, value }) => {
                setIsNotRead(value)
                fetchData(0, value)
              }}
            />
          </SwitchWrapper>
        </Row>
        <OfferApplicationList
          offerId={offerId}
          data={data}
          loading={loading}
          totalPages={totalPages}
          page={page}
          setPage={setPage}
        />
      </Wrapper>
      <Outlet />
    </>
  )
}

export default OfferApplicationModal

const Wrapper = styled(Box)`
  margin-top: ${({ theme }) => theme.space.M};
`

const SwitchWrapper = styled(Row)`
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  padding: ${({ theme }) => theme.space.S};
  background: ${({ theme }) => theme.color.background};
  justify-content: space-between;
  align-items: center;
`
